import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { BsCheck2Circle, BsArrowRight, BsQuestionCircle, BsBuilding } from 'react-icons/bs';
import GlobalCta from '../../Components/GlobalCta/GlobalCta';

const industryDatabase = {
  healthcare: {
    title: 'Healthcare & Medical Technology Solutions',
    badge: 'Healthcare Tech',
    headline: 'Centralized Hospital, Patient Portal, and Pharmacy Systems.',
    desc: 'Custom engineered medical software, EMR/EHR integrations, tele-medicine portals, and automated patient appointment pipelines.',
    capabilities: [
      'Patient Appointment & Doctor Scheduling',
      'Electronic Health Records (EHR/EMR) Portal',
      'Pharmacy & Medical Inventory Sync',
      'HIPAA-Compliant Patient Data Security',
      'Automated WhatsApp & SMS Appointment Reminders'
    ]
  },
  ecommerce: {
    title: 'E-Commerce & Retail Growth Engineering',
    badge: 'Retail Tech',
    headline: 'High-Speed Online Commerce Engines Built for Global Conversion.',
    desc: 'Custom multi-currency e-commerce platforms, payment gateways, inventory sync engines, and automated multi-channel marketing.',
    capabilities: [
      'Bespoke Multi-Storefront Web Architecture',
      'Real-Time Multi-Warehouse Inventory Sync',
      'Automated Cart Abandonment Recovery',
      'Custom ERP & Payment Gateway Connectors',
      'Sub-Second Mobile Checkout Optimization'
    ]
  },
  hospitality: {
    title: 'Hospitality & Property Management Systems',
    badge: 'Hospitality Tech',
    headline: 'Seamless Guest Experiences and Automated Property Control.',
    desc: 'Custom hotel reservation systems, guest portals, automated check-in messaging, and channel manager integrations.',
    capabilities: [
      'Custom Booking Engine & Direct Channel Sync',
      'Guest WhatsApp Messaging & Concierge Bot',
      'Housekeeping & Maintenance Tracking',
      'Integrated Multi-Property Financials'
    ]
  },
  'real-estate': {
    title: 'Real Estate & Property CRM Technology',
    badge: 'Real Estate Tech',
    headline: 'Property Listing Engines, Broker CRM, and Tenant Portals.',
    desc: 'Centralized property management software, lead qualification pipelines, automated site tour booking, and tenant payment portals.',
    capabilities: [
      'Property Inventory & Listing Management',
      'Automated Lead Qualification for Agents',
      'Virtual Tour & Document Signing Portals',
      'Tenant Maintenance & Billing Engine'
    ]
  },
  manufacturing: {
    title: 'Manufacturing & Industrial ERP Systems',
    badge: 'Industrial Tech',
    headline: 'Factory Floor Automation, Supply Chain, and Inventory ERP.',
    desc: 'Integrated enterprise resource planning software connecting supply chain logistics, shop floor tracking, and equipment maintenance.',
    capabilities: [
      'Production Line & Order Scheduling',
      'Supply Chain & Raw Material Inventory Control',
      'Equipment Preventative Maintenance Alerts',
      'Real-Time Operational Analytics Matrix'
    ]
  },
  logistics: {
    title: 'Logistics & Transportation Software',
    badge: 'Logistics Tech',
    headline: 'Real-Time Dispatch Control, Fleet Tracking, and Freight Portals.',
    desc: 'Custom fleet management systems, automated dispatch routing, driver portals, and real-time cargo tracking APIs.',
    capabilities: [
      'Real-Time GPS & Dispatch Control Center',
      'Driver Mobile Portals & Electronic Proof of Delivery',
      'Route Optimization & Fuel Tracking',
      'Automated Shipper & Consignee Notifications'
    ]
  },
  education: {
    title: 'Education & EdTech Learning Platforms',
    badge: 'EdTech Solutions',
    headline: 'Student Portals, Learning Management, and Academic Analytics.',
    desc: 'Scalable LMS platforms, virtual classroom infrastructure, student enrollment systems, and automated grading matrixes.',
    capabilities: [
      'Interactive Student & Faculty Portals',
      'Course Content & Video Streaming Engine',
      'Automated Enrollment & Fee Payment Gateway',
      'Academic Performance & Grading Matrix'
    ]
  },
  'professional-services': {
    title: 'Professional Services & Corporate Tech',
    badge: 'Enterprise Services',
    headline: 'Custom Enterprise Portals, Billing, and Team Task Intelligence.',
    desc: 'Client portals, automated billing engines, contract management, and internal team project execution software.',
    capabilities: [
      'Secure Client Collaboration Portals',
      'Time Tracking & Automated Invoicing',
      'Document Approval & Contract Workflow',
      'Resource Utilization & Capacity Analytics'
    ]
  }
};

const IndustryDetail = () => {
  const { industryId } = useParams();
  const industry = industryDatabase[industryId] || industryDatabase['healthcare'];

  return (
    <div className="bg-slate-950 text-white min-h-screen font-sans selection:bg-[#f06600] selection:text-white pt-10">
      
      {/* Hero Header */}
      <section className="py-16 bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-widest font-extrabold text-[#f06600] bg-orange-950/60 border border-orange-900/40 px-3.5 py-1.5 rounded-full inline-block mb-4">
            {industry.badge}
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight max-w-4xl">
            {industry.title}
          </h1>
          <p className="text-xl text-[#f06600] font-semibold mt-3 max-w-3xl">
            {industry.headline}
          </p>
          <p className="text-slate-300 text-base mt-4 max-w-3xl leading-relaxed">
            {industry.desc}
          </p>

          <div className="mt-8">
            <Link to="/contactus">
              <button className="bg-[#f06600] hover:bg-orange-600 text-white font-bold px-7 py-3.5 rounded-xl shadow-lg shadow-orange-500/25 flex items-center gap-2 text-sm">
                <span>Discuss {industry.badge} Architecture</span>
                <BsArrowRight />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-8">
            Specialized Capabilities & Systems
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industry.capabilities.map((cap, idx) => (
              <div key={idx} className="bg-slate-900 border border-slate-800 p-6 rounded-2xl flex items-start gap-3 shadow-sm">
                <BsCheck2Circle className="text-[#f06600] text-xl shrink-0 mt-0.5" />
                <span className="text-slate-200 text-sm font-semibold leading-snug">{cap}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GlobalCta />

    </div>
  );
};

export default IndustryDetail;
